/** The row that stands at the top of every admin screen.
 *
 * One title, one segmented control, and a single sentence about the merge
 * job on the right. It lives here rather than in each screen because the
 * panel is one place with ten views of it: the tabs have to be the same ten
 * in the same order wherever you are, and the job line has to say the same
 * thing on Costs as it does on the Dashboard.
 *
 * The count on the Issues tab is the panel's only nag, so it is drawn from
 * whatever the caller knows best. A screen that has just counted the queue
 * passes its own figure; anything else takes the shared reading from
 * `adminPulse`, which is one cached request for every header on the site.
 *
 * The job line reports rather than reassures. A run that has not finished
 * says so, a run that failed says so in the error hue, and a server with no
 * job run recorded at all says that instead of printing a plausible dash —
 * "never run" and "ran and told us nothing" are different facts about a
 * deployment.
 */

import type { ReactNode } from 'react';
import { SegmentedNav } from '@/components/ui';
import { duration } from '@/lib/format';
import type { JobRun } from '@/lib/types';
import { useAdminPulse } from './adminPulse';

/** The panel: the design's five, with the spec's five data screens between
 *  the work (issues, merge requests) and the configuration (settings, costs). */
export const ADMIN_TABS = [
  { to: '/admin/dashboard', label: 'Dashboard' },
  { to: '/admin/issues', label: 'Issues' },
  { to: '/admin/merge', label: 'Merge requests' },
  { to: '/admin/tracks', label: 'Track editor' },
  { to: '/admin/uploads', label: 'Uploads' },
  { to: '/admin/users', label: 'Users' },
  { to: '/admin/sessions', label: 'Sessions' },
  { to: '/admin/live', label: 'Live' },
  { to: '/admin/logs', label: 'Logs' },
  { to: '/admin/settings', label: 'Settings' },
  { to: '/admin/costs', label: 'Costs' },
] as const;

/** `02:00 UTC`. The job runs on a cron in UTC and is discussed in UTC; a
 *  local rendering would have two admins in two countries disagreeing about
 *  when last night's run was. */
function utcClock(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return null;
  const when = new Date(at);
  const hours = String(when.getUTCHours()).padStart(2, '0');
  const minutes = String(when.getUTCMinutes()).padStart(2, '0');
  return `${hours}:${minutes} UTC`;
}

interface JobLine {
  /** Everything before the outcome word. */
  lead: string;
  outcome: string;
  colour: string;
}

/**
 * How long a run may go without a finish time and still be called running.
 * GitHub ends a job at six hours whatever it is doing, so past that the run is
 * not running: it died, or was cancelled, before it could report.
 */
const RUNNING_FOR_MS = 6 * 60 * 60 * 1000;

function jobLine(run: JobRun | null): JobLine | null {
  if (!run) return null;

  const started = utcClock(run.started_at);
  if (!run.finished_at) {
    const began = Date.parse(run.started_at);
    if (Number.isFinite(began) && Date.now() - began > RUNNING_FOR_MS) {
      return {
        lead: started ? `job started ${started} · ` : 'job ',
        outcome: 'never reported back',
        colour: 'var(--color-neutral-500)',
      };
    }
    return {
      lead: started ? `job started ${started} · ` : 'job ',
      outcome: 'running',
      colour: 'var(--color-warn)',
    };
  }

  const took = run.duration_s === null ? null : duration(run.duration_s * 1000);
  const bits = [started, took].filter((bit): bit is string => Boolean(bit));
  const lead = `job last ran ${bits.join(' · ')}${bits.length > 0 ? ' · ' : ''}`;

  if (run.ok === true) return { lead, outcome: 'ok', colour: 'var(--color-ok)' };
  if (run.ok === false) return { lead, outcome: 'failed', colour: 'var(--color-error)' };
  return { lead, outcome: 'no result', colour: 'var(--color-neutral-500)' };
}

export interface AdminHeaderProps {
  /** The screen's own count, when it has a fresher one than the shared
   *  reading. Omit to take the shared one. */
  openIssues?: number;
  /** The screen's own last run. Pass `null` to say there is none; omit to
   *  take the shared one. */
  lastRun?: JobRun | null;
  /** Anything the screen wants between the tabs and the job line. */
  children?: ReactNode;
}

export function AdminHeader({ openIssues, lastRun, children }: AdminHeaderProps) {
  // Only ask for the shared reading when the screen has not supplied both
  // halves of it. `lastRun` may legitimately be null, so `undefined` — not
  // falsiness — is what "the caller did not say" means here.
  const supplied = openIssues !== undefined && lastRun !== undefined;
  const { pulse, loading } = useAdminPulse(!supplied);

  // `/api/me` is the account, not the inbox: it has never carried a count and
  // is not asked for one. What the screen supplied wins, then the shared
  // reading from the dashboard.
  const count = openIssues ?? pulse?.openIssues ?? 0;
  const run = lastRun !== undefined ? lastRun : (pulse?.lastRun ?? null);
  const line = jobLine(run);
  // "Never run" is a claim about the deployment and is not made while the
  // answer is still in the post.
  const waiting = !line && lastRun === undefined && loading;

  const items = ADMIN_TABS.map((tab) =>
    tab.label === 'Issues'
      ? {
          to: tab.to,
          label: tab.label,
          badge: (
            <span
              className="tag tag-accent tabular-nums"
              style={{ fontSize: 9, padding: '0 6px', marginLeft: 4 }}
            >
              <span className="sr-only">open issues:&nbsp;</span>
              {count}
            </span>
          ),
        }
      : { to: tab.to, label: tab.label },
  );

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
      <h1 style={{ fontSize: 24, margin: 0 }}>Admin</h1>

      <SegmentedNav items={items} label="Admin sections" className="seg-wrap" />

      {children}

      <span
        className="tabular-nums"
        style={{
          marginLeft: 'auto',
          fontSize: 11.5,
          color: 'var(--color-neutral-600)',
          whiteSpace: 'nowrap',
        }}
      >
        {line ? (
          <>
            {line.lead}
            <span style={{ color: line.colour }}>{line.outcome}</span>
          </>
        ) : waiting ? null : (
          'no job run recorded yet'
        )}
      </span>
    </div>
  );
}

export default AdminHeader;
